import { ReloadOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  CurrentUserInfo,
  ManagedUserRole,
  ManagedUserStatus,
  ManagedUserSummary,
} from "../shared/types/UserManagement";
import { call } from "../tsrpc";

const { Text, Title } = Typography;
type EditableUserRole = Exclude<ManagedUserRole, "superadmin">;

const ROLE_OPTIONS: Array<{ label: string; value: ManagedUserRole }> = [
  { label: "玩家", value: "player" },
  { label: "游戏管理员", value: "gm" },
  { label: "管理员", value: "admin" },
  { label: "超级管理员", value: "superadmin" },
];

const EDITABLE_ROLE_OPTIONS: Array<{ label: string; value: EditableUserRole }> = [
  { label: "玩家", value: "player" },
  { label: "游戏管理员", value: "gm" },
  { label: "管理员", value: "admin" },
];

const STATUS_OPTIONS: Array<{ label: string; value: ManagedUserStatus }> = [
  { label: "正常", value: "active" },
  { label: "已封禁", value: "banned" },
  { label: "已删除", value: "deleted" },
];

const PageWrap = styled.div`
  min-height: 100%;
  padding: 16px;
  background: #f5f7fb;

  @media (max-width: 768px) {
    padding: 8px;
    overflow-x: hidden;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    align-items: center;

    .ant-typography-secondary {
      display: block;
      max-width: 220px;
    }
  }
`;

const FilterPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;

  @media (max-width: 600px) {
    padding: 10px;

    > .ant-input-group-wrapper,
    > .ant-select {
      width: 100% !important;
      min-width: 0 !important;
    }
  }
`;

const TablePanel = styled.div`
  padding: 0 12px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0 6px 8px;

    .ant-table-content,
    .ant-table-body {
      overflow-x: auto !important;
      -webkit-overflow-scrolling: touch;
    }
  }
`;

interface PageUserManagementProps {
  currentUser?: CurrentUserInfo;
  currentUserLoading: boolean;
}

export default function PageUserManagement({
  currentUser,
  currentUserLoading,
}: PageUserManagementProps) {
  const [messageApi, messageContext] = message.useMessage();
  const [modal, modalContext] = Modal.useModal();
  const [users, setUsers] = useState<ManagedUserSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingIds, setUpdatingIds] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [roles, setRoles] = useState<ManagedUserRole[]>([]);
  const [statuses, setStatuses] = useState<ManagedUserStatus[]>([]);
  const [sortField, setSortField] = useState<"createdAt" | "lastLoginAt">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const canManage = currentUser?.role === "superadmin";

  const loadUsers = useCallback(async () => {
    if (!canManage) return;
    setLoading(true);
    try {
      const response = await call("editor/GetManagedUsers", {
        timestamp: Date.now(),
        token: localStorage.getItem("token") || "",
        page,
        pageSize,
        keyword: keyword || undefined,
        roles: roles.length ? roles : undefined,
        statuses: statuses.length ? statuses : undefined,
        sortField,
        sortOrder,
      });
      if (!response.isSucc) throw new Error(response.err.message);
      setUsers(response.res.data.users);
      setTotal(response.res.data.total);
    } catch (error: any) {
      messageApi.error(error?.message || "用户数据加载失败");
    } finally {
      setLoading(false);
    }
  }, [canManage, keyword, messageApi, page, pageSize, roles, sortField, sortOrder, statuses]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const updateUser = useCallback(async (
    user: ManagedUserSummary,
    update: { role?: EditableUserRole; status?: "active" | "banned" },
  ) => {
    setUpdatingIds(current => [...current, user._id]);
    try {
      const response = await call("editor/UpdateManagedUser", {
        timestamp: Date.now(),
        token: localStorage.getItem("token") || "",
        userId: user._id,
        ...update,
      });
      if (!response.isSucc) throw new Error(response.err.message);
      setUsers(current => current.map(item => item._id === user._id ? response.res.data : item));
      messageApi.success("用户信息已更新");
    } catch (error: any) {
      messageApi.error(error?.message || "用户信息更新失败");
    } finally {
      setUpdatingIds(current => current.filter(id => id !== user._id));
    }
  }, [messageApi]);

  const columns = useMemo<ColumnsType<ManagedUserSummary>>(() => [
    {
      title: "用户名",
      dataIndex: "username",
      width: 150,
      render: value => value || "-",
    },
    {
      title: "昵称",
      dataIndex: "nickname",
      width: 140,
      render: value => value || "-",
    },
    {
      title: "联系方式",
      width: 220,
      render: (_, user) => (
        <Space direction="vertical" size={0}>
          <Text>{user.email || "-"}</Text>
          <Text type="secondary">{user.phone || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "角色",
      dataIndex: "role",
      width: 170,
      render: (_, user) => {
        const readOnly = isReadOnlyUser(user, currentUser);
        if (readOnly) {
          return <Tag color={user.role === "superadmin" ? "purple" : undefined}>{roleLabel(user.role)}</Tag>;
        }
        return (
          <Select
            size="small"
            value={user.role as EditableUserRole}
            options={EDITABLE_ROLE_OPTIONS}
            disabled={updatingIds.includes(user._id)}
            style={{ width: 140 }}
            onChange={nextRole => {
              modal.confirm({
                title: "确认修改用户角色？",
                content: `将“${user.username || user.nickname || user._id}”的角色修改为“${roleLabel(nextRole)}”。`,
                okText: "确认修改",
                cancelText: "取消",
                onOk: () => updateUser(user, { role: nextRole }),
              });
            }}
          />
        );
      },
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 100,
      render: status => renderStatus(status),
    },
    {
      title: "注册时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      sorter: true,
      sortOrder: sortField === "createdAt" ? toTableSortOrder(sortOrder) : null,
      render: formatDateTime,
    },
    {
      title: "最后登录",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width: 170,
      sorter: true,
      sortOrder: sortField === "lastLoginAt" ? toTableSortOrder(sortOrder) : null,
      render: formatDateTime,
    },
    {
      title: "操作",
      fixed: "right",
      width: 110,
      render: (_, user) => {
        if (isReadOnlyUser(user, currentUser)) return <Text type="secondary">只读</Text>;
        const willBan = user.status === "active";
        return (
          <Popconfirm
            title={willBan ? "确认封禁该用户？" : "确认激活该用户？"}
            description={willBan ? "封禁后，该用户现有登录令牌也将无法继续访问。" : "激活后，该用户可以正常登录。"}
            okText={willBan ? "确认封禁" : "确认激活"}
            okButtonProps={{ danger: willBan }}
            cancelText="取消"
            onConfirm={() => updateUser(user, { status: willBan ? "banned" : "active" })}
          >
            <Button
              danger={willBan}
              size="small"
              loading={updatingIds.includes(user._id)}
            >
              {willBan ? "封禁" : "激活"}
            </Button>
          </Popconfirm>
        );
      },
    },
  ], [currentUser, modal, sortField, sortOrder, updateUser, updatingIds]);

  if (currentUserLoading) {
    return <Spin fullscreen tip="正在验证管理权限" />;
  }

  if (!canManage) {
    return (
      <Result
        status="403"
        title="无权访问"
        subTitle="用户管理仅对超级管理员开放。"
      />
    );
  }

  const onTableChange: NonNullable<TableProps<ManagedUserSummary>["onChange"]> = (
    pagination,
    _filters,
    sorter,
  ) => {
    const nextPageSize = pagination.pageSize || pageSize;
    setPageSize(nextPageSize);
    setPage(nextPageSize === pageSize ? pagination.current || 1 : 1);

    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (currentSorter?.field === "createdAt" || currentSorter?.field === "lastLoginAt") {
      setSortField(currentSorter.field);
      setSortOrder(currentSorter.order === "ascend" ? "asc" : "desc");
    }
  };

  return (
    <PageWrap>
      {messageContext}
      {modalContext}
      <Header>
        <div>
          <Title level={4} style={{ margin: 0 }}>用户管理</Title>
          <Text type="secondary">管理后台账号的角色与登录状态</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => void loadUsers()}>
          刷新
        </Button>
      </Header>

      <FilterPanel>
        <Input.Search
          allowClear
          value={searchInput}
          placeholder="搜索用户名、昵称、邮箱或手机号"
          style={{ width: 320 }}
          onChange={event => setSearchInput(event.target.value)}
          onSearch={value => {
            setPage(1);
            setKeyword(value.trim());
          }}
        />
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          value={roles}
          options={ROLE_OPTIONS}
          placeholder="全部角色"
          style={{ minWidth: 220 }}
          onChange={value => {
            setPage(1);
            setRoles(value);
          }}
        />
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          value={statuses}
          options={STATUS_OPTIONS}
          placeholder="全部状态"
          style={{ minWidth: 200 }}
          onChange={value => {
            setPage(1);
            setStatuses(value);
          }}
        />
        <Text type="secondary">共 {total} 个用户</Text>
      </FilterPanel>

      <TablePanel>
        <Table<ManagedUserSummary>
          rowKey="_id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={users}
          scroll={{ x: 1320 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: value => `共 ${value} 条`,
          }}
          onChange={onTableChange}
        />
      </TablePanel>
    </PageWrap>
  );
}

function isReadOnlyUser(user: ManagedUserSummary, currentUser?: CurrentUserInfo): boolean {
  return user.role === "superadmin" || user.status === "deleted" || user._id === currentUser?._id;
}

function roleLabel(role: ManagedUserRole): string {
  return ROLE_OPTIONS.find(option => option.value === role)?.label || role;
}

function renderStatus(status: ManagedUserStatus) {
  if (status === "active") return <Tag color="green">正常</Tag>;
  if (status === "banned") return <Tag color="red">已封禁</Tag>;
  return <Tag>已删除</Tag>;
}

function formatDateTime(value?: number): string {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-";
}

function toTableSortOrder(order: "asc" | "desc"): "ascend" | "descend" {
  return order === "asc" ? "ascend" : "descend";
}
